import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useIntl } from 'react-intl'
import { Search, Package, CornerDownLeft, Settings as SettingsIcon } from 'lucide-react'
import { createPortal } from 'react-dom'
import { cn } from '../utils/cn'
import { useFocusTrap } from '../components/ui/useFocusTrap'
import { Kbd, MOD_KEY } from '../components/ui/Kbd'
import { Badge } from '../components/ui/Badge'
import { visibleNavItems, visibleSettingsNav } from '../navigation'
import { useAuthStore } from '../stores/authStore'
import { useFeatures } from '../hooks/useFeatures'
import { searchLoads } from '../services/api/search'

interface PaletteItem {
  id: string
  group: 'navigate' | 'settings' | 'loads' | 'actions'
  label: string
  hint?: string
  icon?: React.ReactNode
  badge?: React.ReactNode
  run: () => void
}

/**
 * Command palette — Cmd/Ctrl+K. Navigate to any page/settings screen, or
 * open a load by number (uses the assignments search API).
 */
export function CommandPalette({ open, onClose }: { open: boolean; onClose: () => void }) {
  const intl = useIntl()
  const navigate = useNavigate()
  const { role } = useAuthStore()
  const { features } = useFeatures()
  const [query, setQuery] = useState('')
  const [active, setActive] = useState(0)
  const panelRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const listRef = useRef<HTMLUListElement>(null)
  useFocusTrap(panelRef, open, { onEscape: onClose, initialFocus: inputRef })

  useEffect(() => { if (open) { setQuery(''); setActive(0) } }, [open])

  const [debounced, setDebounced] = useState('')
  useEffect(() => { const t = setTimeout(() => setDebounced(query.trim()), 200); return () => clearTimeout(t) }, [query])

  const looksLikeLoad = debounced.length >= 2
  const { data: loadHits = [], isFetching } = useQuery({
    queryKey: ['palette-loads', debounced],
    queryFn: () => searchLoads(debounced, 8),
    enabled: open && looksLikeLoad,
    staleTime: 15_000,
    retry: false,
  })

  const items = useMemo<PaletteItem[]>(() => {
    const q = query.trim().toLowerCase()
    const go = (path: string) => () => { onClose(); navigate(path) }
    const nav: PaletteItem[] = visibleNavItems(role, features).map(n => ({
      id: `nav-${n.id}`, group: 'navigate',
      label: intl.formatMessage({ id: n.labelId, defaultMessage: n.label }),
      hint: n.path, icon: <n.icon className="w-4 h-4" aria-hidden />, run: go(n.path),
    }))
    const settings: PaletteItem[] = visibleSettingsNav(role).flatMap(g => g.items.map(i => ({
      id: `settings-${i.id}`, group: 'settings' as const,
      label: intl.formatMessage({ id: i.labelId, defaultMessage: i.label }),
      hint: `${intl.formatMessage({ id: g.labelId, defaultMessage: g.label })} · ${i.keywords ?? ''}`.trim(),
      icon: <SettingsIcon className="w-4 h-4" aria-hidden />, run: go(i.path),
    })))
    const loads: PaletteItem[] = loadHits.map(h => ({
      id: `load-${h.id}`, group: 'loads',
      label: h.load_number,
      hint: [h.driver_name, h.origin, h.destination].filter(Boolean).join(' · '),
      icon: <Package className="w-4 h-4" aria-hidden />,
      badge: h.status ? <Badge variant="status" value={h.status} /> : undefined,
      run: go(`/loads/${h.id}`),
    }))
    const match = (i: PaletteItem) => !q || i.label.toLowerCase().includes(q) || (i.hint ?? '').toLowerCase().includes(q)
    return [...loads, ...nav.filter(match), ...settings.filter(match)]
  }, [query, role, features, loadHits, navigate, onClose, intl])

  useEffect(() => { setActive(0) }, [items.length, query])

  useEffect(() => {
    const el = listRef.current?.querySelector<HTMLElement>(`[data-index="${active}"]`)
    el?.scrollIntoView({ block: 'nearest' })
  }, [active])

  if (!open || typeof document === 'undefined') return null

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive(a => Math.min(items.length - 1, a + 1)) }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive(a => Math.max(0, a - 1)) }
    else if (e.key === 'Enter') { e.preventDefault(); items[active]?.run() }
  }

  const groupLabel: Record<PaletteItem['group'], string> = {
    loads: intl.formatMessage({ id: 'palette.group.loads', defaultMessage: 'Loads' }),
    navigate: intl.formatMessage({ id: 'palette.group.navigate', defaultMessage: 'Go to' }),
    settings: intl.formatMessage({ id: 'palette.group.settings', defaultMessage: 'Settings' }),
    actions: intl.formatMessage({ id: 'palette.group.actions', defaultMessage: 'Actions' }),
  }

  let lastGroup: PaletteItem['group'] | null = null

  return createPortal(
    <div className="fixed inset-0 z-[80] flex items-start justify-center pt-[12vh] px-4">
      <div className="absolute inset-0 bg-black/40 animate-fade-in" onClick={onClose} aria-hidden />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={intl.formatMessage({ id: 'palette.title', defaultMessage: 'Command palette' })}
        tabIndex={-1}
        onKeyDown={onKeyDown}
        className="relative w-full max-w-xl bg-surface rounded-xl shadow-2xl border border-line overflow-hidden animate-fade-in outline-none"
      >
        <div className="flex items-center gap-2 px-3 border-b border-line">
          <Search className="w-4 h-4 text-fg-subtle shrink-0" aria-hidden />
          <input
            ref={inputRef}
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder={intl.formatMessage({ id: 'palette.placeholder', defaultMessage: 'Type a page, setting or load number…' })}
            aria-label={intl.formatMessage({ id: 'palette.placeholder', defaultMessage: 'Type a page, setting or load number…' })}
            role="combobox"
            aria-expanded
            aria-controls="palette-list"
            aria-activedescendant={items[active] ? `palette-${items[active].id}` : undefined}
            className="flex-1 h-12 bg-transparent text-sm text-fg outline-none placeholder:text-fg-subtle"
          />
          {isFetching && <span className="text-xs text-fg-subtle">…</span>}
          <Kbd>Esc</Kbd>
        </div>
        <ul ref={listRef} id="palette-list" role="listbox" className="max-h-[50vh] overflow-y-auto py-1">
          {items.length === 0 && (
            <li className="px-4 py-6 text-sm text-fg-muted text-center">
              {looksLikeLoad && isFetching
                ? intl.formatMessage({ id: 'palette.searching', defaultMessage: 'Searching…' })
                : intl.formatMessage({ id: 'palette.empty', defaultMessage: 'No matches' })}
            </li>
          )}
          {items.map((item, i) => {
            const header = item.group !== lastGroup ? groupLabel[item.group] : null
            lastGroup = item.group
            return (
              <li key={item.id} role="presentation">
                {header && <div className="px-4 pt-2 pb-1 text-xs font-semibold uppercase tracking-wide text-fg-subtle">{header}</div>}
                <button
                  id={`palette-${item.id}`}
                  role="option"
                  aria-selected={i === active}
                  data-index={i}
                  type="button"
                  onMouseEnter={() => setActive(i)}
                  onClick={item.run}
                  className={cn('w-full flex items-center gap-3 px-4 py-2 text-left text-sm', i === active ? 'bg-brand-50 text-brand-700' : 'text-fg hover:bg-surface-sunken')}
                >
                  <span className="shrink-0 text-fg-subtle">{item.icon}</span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate font-medium">{item.label}</span>
                    {item.hint && <span className="block truncate text-xs text-fg-subtle">{item.hint}</span>}
                  </span>
                  {item.badge}
                  {i === active && <CornerDownLeft className="w-3.5 h-3.5 text-fg-subtle shrink-0" aria-hidden />}
                </button>
              </li>
            )
          })}
        </ul>
        <div className="flex items-center gap-3 px-4 py-2 border-t border-line text-xs text-fg-subtle bg-surface-sunken/60">
          <span className="flex items-center gap-1"><Kbd>↑</Kbd><Kbd>↓</Kbd> {intl.formatMessage({ id: 'palette.navigateHint', defaultMessage: 'navigate' })}</span>
          <span className="flex items-center gap-1"><Kbd>↵</Kbd> {intl.formatMessage({ id: 'palette.openHint', defaultMessage: 'open' })}</span>
          <span className="ml-auto flex items-center gap-1"><Kbd>{MOD_KEY}</Kbd><Kbd>K</Kbd></span>
        </div>
      </div>
    </div>,
    document.body,
  )
}

export default CommandPalette
